<template>
    <div class="service-home">
        <a-layout-header class="home-header">
            <div class="header-left">
                <a-badge status="processing">
                    <span class="badge-text">TAX AGENT</span>
                </a-badge>
            </div>
            <div class="header-center">
                <a-space :size="40">
                    <a-button type="text" :class="{ 'active': activeTab === 'home' }" @click="switchToHome">
                        <template #icon>
                            <HomeOutlined />
                        </template>
                    </a-button>
                    <a-button type="text" :class="{ 'active': activeTab === 'chat' }" @click="activeTab = 'chat'">
                        聊天
                    </a-button>
                    <a-button type="text" :class="{ 'active': activeTab === 'case' }"
                        @click="activeTab = 'case'">
                        案例库
                    </a-button>
                    <a-button type="text" :class="{ 'active': activeTab === 'team' }" @click="activeTab = 'team'">
                        专业团队
                    </a-button>
                    <a-button type="text" :class="{ 'active': activeTab === 'files' }" @click="activeTab = 'files'">
                        文件管理
                    </a-button>
                </a-space>
            </div>
            <div class="header-right">
                <a-space :size="16">
                    <a-button type="text">
                        <BellOutlined />
                    </a-button>
                    <a-button type="text">
                        <SlidersOutlined />
                    </a-button>
                    <BizUserProfile />
                </a-space>
            </div>
        </a-layout-header>

        <!-- ===== 首页内容 ===== -->
        <div v-if="activeTab === 'home'" class="home-content">
            <div class="main-content">
                <div class="welcome-section">
                    <h1 class="welcome-title">
                        欢迎来到 <span class="title-highlight">TAX AGENT</span>
                    </h1>
                    <p class="welcome-text">
                        您可以使用 TAX AGENT 来分析您的税务问题，获取专业的税务建议。
                    </p>
                </div>

                <div class="section">
                    <div class="section-header">
                        <a-space>
                            <MessageOutlined />
                            <span>聊天</span>
                        </a-space>
                    </div>
                    <div class="chat-list">
                        <template v-if="loadingChats">
                            <div class="chat-card skeleton" v-for="n in 3" :key="'skeleton-' + n">
                                <div class="chat-content">
                                    <div class="skeleton-avatar"></div>
                                    <div class="skeleton-lines">
                                        <div class="skeleton-line w-60"></div>
                                        <div class="skeleton-line w-40"></div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <a-card class="chat-card" hoverable v-for="(chat, index) in chatList" :key="chat.id || index" @click="openChat(chat)">
                                <div class="chat-content">
                                    <div class="chat-icon">
                                        <a-avatar :size="40">{{ chat.name.charAt(0) }}</a-avatar>
                                    </div>
                                    <div class="chat-info">
                                        <div class="chat-name">{{ chat.name }}</div>
                                        <div class="chat-time">{{ chat.time }}</div>
                                    </div>
                                </div>
                            </a-card>
                            <a-card class="chat-card" hoverable @click="createNewChat">
                                <div class="empty-card">
                                    <PlusOutlined style="font-size: 10px; color: #1890ff;" />
                                    <span style="font-size: 17px;color: #1890ff;">创建新聊天</span>
                                </div>
                            </a-card>
                        </template>
                    </div>
                </div>

                <div class="section">
                    <div class="section-header">
                        <a-space>
                            <DatabaseOutlined />
                            <span>案例库</span>
                        </a-space>
                    </div>
                    <div class="knowledge-grid">
                        <a-card class="knowledge-card" hoverable>
                            <div class="empty-card">
                                <PlusOutlined style="font-size: 24px; color: #999;" />
                                <span>尚未查到案例</span>
                            </div>
                        </a-card>
                        <a-card class="knowledge-card" hoverable>
                            <div class="empty-card">
                                <PlusOutlined style="font-size: 24px; color: #999;" />
                                <span>+</span>
                            </div>
                        </a-card>
                    </div>
                </div>
            </div>

            <div class="expert-sidebar">
                <div class="expert-carousel-wrapper">
                    <div class="carousel-arrow arrow-left" @click="prevExpert">
                        <LeftOutlined />
                    </div>
                    <a-carousel autoplay :autoplay-speed="5000" ref="carouselRef" class="expert-carousel">
                        <div v-for="(expert, index) in expertList" :key="expert.id || index">
                            <a-card class="expert-card">
                                <div class="expert-avatar-wrapper">
                                    <a-avatar :size="48" class="expert-avatar">
                                        <template #icon>
                                            <UserOutlined />
                                        </template>
                                    </a-avatar>
                                </div>
                                <div class="expert-name">{{ expert.name }}</div>
                                <div class="expert-title">{{ expert.title }}</div>
                                <div class="expert-honors">
                                    <a-tag v-for="(honor, i) in splitField(expert.qualification)" :key="i" color="blue">
                                        {{ honor }}
                                    </a-tag>
                                    <a-tag v-if="expert.experience_years" color="orange">
                                        {{ expert.experience_years }}年
                                    </a-tag>
                                </div>
                                <div class="expert-contact">
                                    <div class="contact-item">
                                        <PhoneOutlined />
                                        <span>{{ expert.phone }}</span>
                                    </div>
                                    <div class="contact-item">
                                        <MailOutlined />
                                        <span>{{ expert.email }}</span>
                                    </div>
                                </div>
                                <div class="expert-desc">{{ expert.intro }}</div>
                            </a-card>
                        </div>
                    </a-carousel>
                    <div class="carousel-arrow arrow-right" @click="nextExpert">
                        <RightOutlined />
                    </div>
                </div>
            </div>
        </div>

        <!-- ===== 聊天界面 ===== -->
        <div v-else-if="activeTab === 'chat'" class="faq-container">
            <div class="faq-sidebar">
                <div class="sidebar-header">
                    <span class="sidebar-title">历史对话</span>
                </div>
                <div class="new-chat-btn">
                    <a-button type="primary" block @click="createNewConversation" :loading="creating" :icon="h(PlusOutlined)">
                        新建对话
                    </a-button>
                </div>
                <div class="conversation-list">
                    <a-spin :spinning="loadingConversations" class="conv-spin">
                    <div
                        v-for="conv in conversations"
                        :key="conv.id"
                        class="conversation-item"
                        :class="{ active: conv.id === activeConversationId }"
                        @click="switchConversation(conv.id)"
                    >
                        <div class="conv-info">
                            <div class="conv-name">{{ conv.title }}</div>
                            <div class="conv-preview">{{ conv.lastMessage }}</div>
                            <div class="conv-time">{{ conv.createdAt }}</div>
                        </div>
                        <a-button
                            type="text"
                            size="small"
                            class="conv-delete-btn"
                            :icon="h(DeleteOutlined)"
                            @click.stop="deleteConversationById(conv.id)"
                        />
                    </div>
                    </a-spin>
                </div>
            </div>

            <div class="faq-main">
                <div class="chat-header">
                    <span class="chat-title">{{ activeConversation.title }}</span>
                    <div class="chat-actions">
                        <a-button type="text" danger @click="deleteConversation" :loading="deleting" :icon="h(DeleteOutlined)" />
                    </div>
                </div>

                <div class="chat-messages" ref="messagesRef">
                    <div v-if="loadingMessages" class="chat-loading">
                        <a-spin />
                    </div>
                    <div
                        v-for="msg in activeConversation.messages"
                        :key="msg.id"
                        class="message-row"
                        :class="msg.role"
                    >
                        <div v-if="msg.role === 'assistant'" class="message-avatar">
                            <a-avatar :icon="h(RobotOutlined)" style="background-color: #1890ff;" />
                        </div>
                        <div class="message-bubble" :class="msg.role">
                            <div class="message-content">
                                <template v-for="(seg, idx) in parseContent(msg.content)" :key="idx">
                                    <span v-if="seg.type === 'text'">{{ seg.value }}</span>
                                    <a-tag v-else class="case-tag" @click="showCaseDetail(seg.value)">
                                        {{ seg.value }}
                                    </a-tag>
                                </template>
                            </div>
                            <div class="message-time">{{ msg.createdAt }}</div>
                        </div>
                    </div>
                    <div v-if="aiThinking" class="message-row assistant">
                        <div class="message-avatar">
                            <a-avatar :icon="h(RobotOutlined)" style="background-color: #1890ff;" />
                        </div>
                        <div class="message-bubble assistant thinking-bubble">
                            <div class="thinking-dots">
                                <span class="dot"></span>
                                <span class="dot"></span>
                                <span class="dot"></span>
                                <span class="thinking-text">思考中...</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="chat-input-area">
                    <div class="input-row">
                        <a-textarea
                            v-model:value="inputText"
                            placeholder="请输入您的问题..."
                            :rows="3"
                            :disabled="sending"
                            @keydown.enter.prevent="sendMessage"
                        />
                        <a-button
                            type="primary"
                            class="send-btn"
                            @click="sendMessage"
                            :loading="sending"
                            :disabled="!inputText.trim() || sending"
                            :icon="h(SendOutlined)"
                        >
                            发送
                        </a-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- 案例详情弹窗 -->
        <a-modal
            v-model:open="caseModalVisible"
            title="案例详情"
            width="720px"
            :footer="null"
            destroy-on-close
        >
            <a-spin :spinning="caseLoading" class="case-modal-spin">
                <div v-if="caseDetail" class="case-detail">
                    <div class="case-detail-header">
                        <span class="case-detail-badge">{{ caseDetail.caseNo || '-' }}</span>
                        <span class="case-detail-no">{{ caseDetail.caseName || '-' }}</span>
                    </div>
                    <a-descriptions bordered :column="1" size="middle">
                        <a-descriptions-item
                            v-for="(val, key) in caseFields"
                            :key="key"
                            :label="val"
                        >
                            {{ caseDetail[key] || '-' }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>
                <div v-else-if="!caseLoading" class="case-empty">
                    未找到案例信息
                </div>
            </a-spin>
        </a-modal>

        <!-- ===== 专业团队 ===== -->
        <div v-if="activeTab === 'team'" class="team-content">
            <BizTeamPanel />
        </div>
    </div>
</template>

<script>
import {
    HomeOutlined,
    DatabaseOutlined,
    MessageOutlined,
    PlusOutlined,
    BellOutlined,
    SlidersOutlined,
    UserOutlined,
    PhoneOutlined,
    MailOutlined,
    LeftOutlined,
    RightOutlined,
    SendOutlined,
    DeleteOutlined,
    RobotOutlined,
} from '@ant-design/icons-vue';
import { ref, reactive, onMounted } from 'vue';
import { h } from 'vue';
import {
    getConversationList,
    createConversation,
    sendChatMessage,
    deleteConversationById as deleteConversationByIdApi,
    getConversationMessages,
    getCaseByCaseNo,
    expertTeamList,
} from '@/api/service';
import { message } from 'ant-design-vue';
import BizTeamPanel from './components/BizTeamPanel.vue';
import BizUserProfile from './components/BizUserProfile.vue';

export default {
    name: 'BizHomePage',
    components: {
        HomeOutlined,
        DatabaseOutlined,
        MessageOutlined,
        PlusOutlined,
        BellOutlined,
        SlidersOutlined,
        UserOutlined,
        PhoneOutlined,
        MailOutlined,
        LeftOutlined,
        RightOutlined,
        BizTeamPanel,
        BizUserProfile,
    },
    setup() {
        const activeTab = ref('home');
        const chatTab = ref('chat');
        const loadingChats = ref(false);

        // ===== 首页：聊天卡片 =====
        const chatList = ref([]);

        const fetchChatList = async () => {
            loadingChats.value = true;
            try {
                const response = await getConversationList({ pageNumber: 1, pageSize: 20 });
                const listData = response.data;
                const convList = listData.conversationList || listData.records || [];
                if (Array.isArray(convList)) {
                    chatList.value = convList.map(item => ({
                        id: item.ConversationId || item.id,
                        name: item.Title || item.title || '新对话',
                        time: item.CreateTime || item.createdAt || item.createTime || '',
                        avatar: '',
                    }));
                }
            } catch (e) {
                // ignore
            }
            loadingChats.value = false;
        };

        // 打开聊天（切换 tab + 加载会话）
        const openChat = (chat) => {
            if (chat.id) {
                activeTab.value = 'chat';
                switchConversation(chat.id);
            }
        };

        // 从首页创建新聊天
        const createNewChat = async () => {
            try {
                const response = await createConversation();
                if (response.code === 200 && response.data) {
                    const data = response.data;
                    const convId = data.conversationId || data.ConversationId || data.id;
                    if (convId) {
                        activeTab.value = 'chat';
                        const newConv = {
                            id: convId,
                            title: data.Title || data.title || '新对话',
                            lastMessage: data.lastMessage || '',
                            createdAt: data.CreateTime || data.createTime || data.createdAt || '',
                        };
                        conversations.value.unshift(newConv);
                        activeConversationId.value = convId;
                        conversationCache[convId] = [];
                        updateActiveConversation({
                            id: convId,
                            title: newConv.title,
                            lastMessage: '',
                            createdAt: newConv.createdAt,
                            messages: [],
                        });
                    } else {
                        message.error('创建对话失败：返回数据异常');
                    }
                }
            } catch (e) {
                message.error('创建对话失败');
            }
        };

        const switchToHome = () => {
            activeTab.value = 'home';
        };

        // ===== 首页：专家轮播 =====
        const expertList = ref([]);
        const expertLoading = ref(false);

        const fetchExpertList = async () => {
            expertLoading.value = true;
            try {
                const response = await expertTeamList();
                if (response.code === 200 && response.data) {
                    expertList.value = Array.isArray(response.data) ? response.data : [];
                    // 限制最多显示 10 条
                    if (expertList.value.length > 10) {
                        expertList.value = expertList.value.slice(0, 10);
                    }
                }
            } catch (e) {
                console.error('获取专家列表失败', e);
            }
            expertLoading.value = false;
        };

        // 分割资质/擅长领域的工具函数
        const splitField = (val) => {
            if (!val) return [];
            return val.split(/[,，、]/).filter(Boolean);
        };

        const carouselRef = ref(null);
        const prevExpert = () => {
            if (carouselRef.value) {
                carouselRef.value.prev();
            }
        };
        const nextExpert = () => {
            if (carouselRef.value) {
                carouselRef.value.next();
            }
        };

        // ===== 聊天功能 =====
        const conversations = ref([]);
        const activeConversationId = ref(null);
        const inputText = ref('');
        const messagesRef = ref(null);
        const creating = ref(false);
        const deleting = ref(false);
        const sending = ref(false);
        const loadingMessages = ref(false);
        const aiThinking = ref(false);
        const loadingConversations = ref(false);
        const conversationCache = reactive({});

        // 案例详情弹窗
        const caseModalVisible = ref(false);
        const caseLoading = ref(false);
        const caseDetail = ref(null);
        const caseFields = {
            caseName: '案例名称',
            keyword: '关键词',
            taxType: '涉及税种',
            industry: '所属行业',
            taxpayerType: '纳税人类型',
            caseBackground: '案例背景',
            caseSource: '案例来源',
            involvedAmount: '涉及金额',
            coreQuestion: '核心问题',
            analysisProcess: '分析过程',
            handlingResult: '处理结果',
            policyBasis: '政策依据',
            status: '状态',
            createdAt: '创建时间',
        };

        const activeConversation = reactive({
            id: null,
            title: '',
            lastMessage: '',
            createdAt: '',
            messages: [],
        });

        const updateActiveConversation = (conv) => {
            activeConversation.id = conv.id;
            activeConversation.title = conv.title || '';
            activeConversation.lastMessage = conv.lastMessage || '';
            activeConversation.createdAt = conv.createdAt || '';
            activeConversation.messages = conv.messages || [];
        };

        const fetchConversations = async () => {
            loadingConversations.value = true;
            try {
                const response = await getConversationList({ pageNumber: 1, pageSize: 20 });
                const listData = response.data;
                const convList = listData.conversationList || listData.records || [];
                if (Array.isArray(convList)) {
                    conversations.value = convList.map(item => ({
                        id: item.ConversationId || item.id,
                        title: item.Title || item.title || '新对话',
                        lastMessage: item.lastMessage || '',
                        createdAt: item.CreateTime || item.createdAt || item.createTime || '',
                    }));
                }
            } catch (e) {
                // ignore
            }
            loadingConversations.value = false;
        };

        const fetchMessages = async (conversationId) => {
            if (!conversationId) return;
            loadingMessages.value = true;
            try {
                const response = await getConversationMessages(conversationId, { pageSize: 50 });
                if (response.code === 200 && response.data) {
                    const messageList = response.data.messageList || [];
                    const messages = messageList.map(item => ({
                        id: item.recordId || item.messageId || item.id,
                        role: item.role === 'assistant' ? 'assistant' : 'user',
                        content: item.contents && item.contents.length > 0
                            ? (item.contents[0].text || '')
                            : (item.content || ''),
                        createdAt: item.createdAt || '',
                    }));
                    conversationCache[conversationId] = messages;
                    const conv = conversations.value.find(c => c.id === conversationId);
                    updateActiveConversation({
                        id: conversationId,
                        title: conv ? conv.title : '',
                        lastMessage: conv ? conv.lastMessage : '',
                        createdAt: conv ? conv.createdAt : '',
                        messages: messages,
                    });
                    scrollToBottom();
                }
            } catch (e) {
                // ignore
            }
            loadingMessages.value = false;
        };

        const switchConversation = async (id) => {
            if (id === activeConversationId.value) return;
            activeConversationId.value = id;

            if (conversationCache[id]) {
                const conv = conversations.value.find(c => c.id === id);
                updateActiveConversation({
                    id: id,
                    title: conv ? conv.title : '',
                    lastMessage: conv ? conv.lastMessage : '',
                    createdAt: conv ? conv.createdAt : '',
                    messages: conversationCache[id],
                });
                scrollToBottom();
            } else {
                updateActiveConversation({
                    id: id,
                    title: '',
                    lastMessage: '',
                    createdAt: '',
                    messages: [],
                });
                await fetchMessages(id);
            }
        };

        const createNewConversation = async () => {
            creating.value = true;
            try {
                const response = await createConversation();
                if (response.code === 200 && response.data) {
                    const data = response.data;
                    const convId = data.conversationId || data.ConversationId || data.id;
                    if (!convId) {
                        message.error('创建对话失败：返回数据异常');
                        creating.value = false;
                        return;
                    }
                    const newConv = {
                        id: convId,
                        title: data.Title || data.title || '新对话',
                        lastMessage: data.lastMessage || '',
                        createdAt: data.CreateTime || data.createTime || data.createdAt || '',
                    };
                    conversations.value.unshift(newConv);
                    activeConversationId.value = convId;
                    conversationCache[convId] = [];
                    updateActiveConversation({
                        id: convId,
                        title: newConv.title,
                        lastMessage: '',
                        createdAt: newConv.createdAt,
                        messages: [],
                    });
                }
            } catch (e) {
                message.error('创建对话失败');
            }
            creating.value = false;
        };

        const deleteConversationById = async (id) => {
            if (!id) return;
            deleting.value = true;
            try {
                const response = await deleteConversationByIdApi(id);
                if (response.code === 200) {
                    const isActive = id === activeConversationId.value;
                    const index = conversations.value.findIndex(c => c.id === id);
                    conversations.value = conversations.value.filter(c => c.id !== id);
                    delete conversationCache[id];
                    if (conversations.value.length === 0) {
                        activeConversationId.value = null;
                        updateActiveConversation({ id: null, title: '', lastMessage: '', createdAt: '', messages: [] });
                    } else if (isActive) {
                        const nextIndex = Math.min(index, conversations.value.length - 1);
                        const nextConv = conversations.value[nextIndex];
                        if (nextConv) {
                            activeConversationId.value = nextConv.id;
                            await switchConversation(nextConv.id);
                        }
                    }
                }
            } catch (e) {
                message.error('删除对话失败');
            }
            deleting.value = false;
        };

        const deleteConversation = async () => {
            if (!activeConversationId.value) return;
            await deleteConversationById(activeConversationId.value);
        };

        const sendMessage = async () => {
            if (sending.value) return;
            const text = inputText.value.trim();
            if (!text) return;

            if (!activeConversationId.value) {
                message.warning('请先创建对话');
                return;
            }

            sending.value = true;
            aiThinking.value = true;
            const userMsg = {
                id: 'temp-' + Date.now(),
                role: 'user',
                content: text,
                createdAt: new Date().toLocaleTimeString('zh-CN', { hour12: false }),
            };

            activeConversation.messages.push(userMsg);
            scrollToBottom();

            try {
                const response = await sendChatMessage({
                    conversationId: activeConversationId.value,
                    message: text,
                });
                if (response.code === 200 && response.data) {
                    const aiMsg = {
                        id: response.data.id || 'ai-' + Date.now(),
                        role: 'assistant',
                        content: response.data.reply || '',
                        createdAt: new Date().toLocaleTimeString('zh-CN', { hour12: false }),
                    };
                    activeConversation.messages.push(aiMsg);
                    const conv = conversations.value.find(c => c.id === activeConversationId.value);
                    if (conv) {
                        conv.lastMessage = text;
                    }
                    scrollToBottom();
                }
            } catch (e) {
                message.error('发送失败');
            }
            aiThinking.value = false;
            sending.value = false;
            inputText.value = '';
        };

        const scrollToBottom = () => {
            setTimeout(() => {
                if (messagesRef.value) {
                    messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
                }
            }, 50);
        };

        const parseContent = (content) => {
            if (!content) return [{ type: 'text', value: '' }];
            const segments = [];
            const regex = /【【(.+?)】】/g;
            let lastIndex = 0;
            let match;
            while ((match = regex.exec(content)) !== null) {
                if (match.index > lastIndex) {
                    segments.push({ type: 'text', value: content.slice(lastIndex, match.index) });
                }
                segments.push({ type: 'case', value: match[1] });
                lastIndex = match.index + match[0].length;
            }
            if (lastIndex < content.length) {
                segments.push({ type: 'text', value: content.slice(lastIndex) });
            }
            return segments.length > 0 ? segments : [{ type: 'text', value: content }];
        };

        const showCaseDetail = async (caseNo) => {
            caseDetail.value = null;
            caseLoading.value = true;
            caseModalVisible.value = true;
            try {
                const response = await getCaseByCaseNo(caseNo);
                if (response.code === 200) {
                    const data = response.data;
                    caseDetail.value = Array.isArray(data) ? data[0] : data;
                }
            } catch (e) {
                // ignore
            }
            caseLoading.value = false;
        };

        onMounted(() => {
            fetchChatList();
            fetchConversations();
            fetchExpertList();
        });

        return {
            activeTab,
            chatTab,
            chatList,
            loadingChats,
            expertList,
            expertLoading,
            splitField,
            carouselRef,
            prevExpert,
            nextExpert,
            openChat,
            createNewChat,
            switchToHome,
            // 聊天
            conversations,
            activeConversationId,
            activeConversation,
            inputText,
            messagesRef,
            creating,
            deleting,
            sending,
            loadingMessages,
            aiThinking,
            loadingConversations,
            deleteConversationById,
            switchConversation,
            createNewConversation,
            deleteConversation,
            sendMessage,
            parseContent,
            showCaseDetail,
            caseModalVisible,
            caseLoading,
            caseDetail,
            caseFields,
            h,
            SendOutlined,
            PlusOutlined,
            DeleteOutlined,
            RobotOutlined,
        };
    }
};
</script>

<style scoped>
.service-home {
    min-height: 100vh;
    background-color: #ffffff;
}

.home-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #ffffff;
    padding: 0 24px;
    border-bottom: 1px solid #f0f0f0;
    height: 64px;
}

.header-left {
    flex: 1;
}

.badge-text {
    font-size: 14px;
    color: #52c41a;
    font-weight: bold;
}

.header-center {
    flex: 2;
    display: flex;
    justify-content: center;
}

.header-right {
    flex: 1;
    display: flex;
    justify-content: flex-end;
}

.header-center :deep(.ant-btn-text) {
    font-size: 16px;
    color: #666;
}

.header-center :deep(.ant-btn-text:hover) {
    color: #1890ff;
}

.header-center :deep(.ant-btn-text.active) {
    color: #1890ff;
    font-weight: bold;
}

/* ===== 首页内容 ===== */
.home-content {
    display: flex;
    padding: 40px 60px;
    gap: 32px;
}

.main-content {
    flex: 1;
}

.expert-sidebar {
    width: 260px;
    align-self: flex-start;
    margin-top: 100px;
    margin-right: -40px;
}

.welcome-section {
    margin-bottom: 48px;
}

.welcome-title {
    font-size: 28px;
    font-weight: bold;
    color: #333;
}

.welcome-text {
    font-size: 13px;
    color: #afaeae;
    margin-top: 10px;
}

.title-highlight {
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.section {
    margin-bottom: 48px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    font-size: 18px;
    font-weight: 500;
    color: #333;
}

.section-tabs :deep(.ant-btn-text) {
    font-size: 14px;
    color: #999;
    margin: 0 8px;
}

.section-tabs :deep(.ant-btn-text.tab-active) {
    color: #333;
    font-weight: 500;
}

.knowledge-grid {
    display: flex;
    gap: 16px;
}

.knowledge-card {
    width: 280px;
    height: 120px;
    border: 1px dashed #d9d9d9;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
}

.knowledge-card:hover {
    border-color: #1890ff;
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
}

.empty-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #999;
    font-size: 16px;
}

.empty-card span {
    margin-top: 8px;
}

.chat-list {
    display: flex;
    gap: 16px;
    align-items: flex-start;
}

.chat-card {
    width: 280px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
}

.chat-card.skeleton {
    background: #fff;
    border: 1px solid #f0f0f0;
    padding: 24px 20px;
    pointer-events: none;
}

.skeleton-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeleton-pulse 1.5s ease-in-out infinite;
    flex-shrink: 0;
}

.skeleton-lines {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.skeleton-line {
    height: 14px;
    border-radius: 4px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-line.w-60 {
    width: 60%;
}

.skeleton-line.w-40 {
    width: 40%;
}

@keyframes skeleton-pulse {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

.chat-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.chat-content {
    display: flex;
    align-items: center;
    gap: 12px;
}

.chat-icon {
    flex-shrink: 0;
}

.chat-icon :deep(.ant-avatar) {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.chat-info {
    flex: 1;
    min-width: 0;
}

.chat-name {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin-bottom: 4px;
}

.chat-time {
    font-size: 14px;
    color: #999;
}

.view-all {
    color: #1890ff;
    font-size: 16px;
    padding: 0;
    margin-top: 4px;
}

.expert-card {
    border-radius: 8px;
    padding: 12px 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    text-align: center;
    height: 300px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.expert-avatar-wrapper {
    margin-bottom: 8px;
}

.expert-avatar {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    font-size: 20px;
}

.expert-name {
    font-size: 16px;
    font-weight: bold;
    color: #333;
    margin-bottom: 4px;
}

.expert-title {
    font-size: 13px;
    color: #1890ff;
    margin-bottom: 8px;
}

.expert-honors {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
    margin-bottom: 10px;
}

.expert-honors :deep(.ant-tag) {
    font-size: 11px;
    padding: 0 6px;
    line-height: 20px;
}

.expert-contact {
    border-top: 1px solid #f0f0f0;
    padding-top: 10px;
    margin-bottom: 10px;
}

.contact-item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
}

.contact-item:last-child {
    margin-bottom: 0;
}

.expert-desc {
    font-size: 12px;
    color: #999;
    line-height: 1.5;
    text-align: center;
}

.expert-carousel {
    height: 340px;
}

.expert-carousel :deep(.slick-slider),
.expert-carousel :deep(.slick-list),
.expert-carousel :deep(.slick-track) {
    height: 100%;
}

.expert-carousel-wrapper {
    position: relative;
}

.carousel-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 12px;
    color: #666;
    transition: all 0.2s;
}

.carousel-arrow:hover {
    background: #1890ff;
    color: #fff;
}

.arrow-left {
    left: -14px;
}

.arrow-right {
    right: -14px;
}

.expert-card :deep(.ant-card-body) {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

/* ===== 聊天界面 ===== */
.faq-container {
    display: flex;
    height: calc(100vh - 64px);
    background: #fff;
    overflow: hidden;
}

.faq-sidebar {
    width: 280px;
    min-width: 280px;
    background: linear-gradient(180deg, #f8f9fc 0%, #f0f2f5 100%);
    border-right: 1px solid #e8e8e8;
    display: flex;
    flex-direction: column;
}

.sidebar-header {
    padding: 24px 20px 8px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.sidebar-header::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    background: #1890ff;
    border-radius: 50%;
}

.sidebar-title {
    font-size: 16px;
    font-weight: 700;
    color: #1a1a2e;
    letter-spacing: 0.5px;
}

.new-chat-btn {
    padding: 12px 16px 16px;
}

.new-chat-btn .ant-btn {
    border-radius: 8px;
    height: 40px;
    font-weight: 500;
    box-shadow: 0 2px 6px rgba(24, 144, 255, 0.2);
    transition: all 0.25s ease;
}

.new-chat-btn .ant-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
}

.conversation-list {
    flex: 1;
    overflow-y: auto;
    padding: 0 10px 12px;
}

.conv-spin {
    width: 100%;
    min-height: 100px;
}

.conversation-list::-webkit-scrollbar {
    width: 4px;
}

.conversation-list::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
}

.conversation-item {
    padding: 14px 14px;
    border-radius: 10px;
    cursor: pointer;
    margin-bottom: 6px;
    transition: all 0.2s ease;
    position: relative;
    border: 1px solid #e8e8e8;
    background: #fff;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
}

.conversation-item:hover .conv-delete-btn {
    opacity: 1;
    pointer-events: auto;
}

.conv-info {
    flex: 1;
    min-width: 0;
    overflow: hidden;
}

.conv-delete-btn {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s;
    flex-shrink: 0;
    margin-top: 2px;
    color: #bfbfbf;
}

.conv-delete-btn:hover {
    color: #ff4d4f !important;
}

.conversation-item:hover {
    background: #fff;
    border-color: #e8e8e8;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.conversation-item.active {
    background: #fff;
    border-color: #1890ff;
    box-shadow: 0 2px 12px rgba(24, 144, 255, 0.12);
}

.conversation-item.active::before {
    content: '';
    position: absolute;
    left: -11px;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 32px;
    background: #1890ff;
    border-radius: 0 3px 3px 0;
}

.conv-name {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.conv-preview {
    font-size: 12px;
    color: #8c8c8c;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: 4px;
    line-height: 1.4;
}

.conv-time {
    font-size: 11px;
    color: #bfbfbf;
}

.faq-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #f5f6fa;
}

.chat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 28px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid #e8e8e8;
    flex-shrink: 0;
}

.chat-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a2e;
    position: relative;
    padding-left: 16px;
}

.chat-title::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 18px;
    background: linear-gradient(180deg, #1890ff, #40a9ff);
    border-radius: 2px;
}

.chat-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.chat-actions .ant-btn-text:hover {
    background: rgba(255, 77, 79, 0.08);
}

.chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 28px 32px;
    scroll-behavior: smooth;
}

.chat-messages::-webkit-scrollbar {
    width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
    background: transparent;
}

.chat-messages::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
    background: #bfbfbf;
}

.chat-loading {
    display: flex;
    justify-content: center;
    padding: 60px 0;
}

.message-row {
    display: flex;
    margin-bottom: 24px;
    align-items: flex-start;
    animation: fadeInUp 0.3s ease;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(12px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.message-row.user {
    justify-content: flex-end;
}

.message-avatar {
    margin-right: 14px;
    flex-shrink: 0;
    margin-top: 4px;
}

.message-bubble {
    max-width: 68%;
    padding: 14px 20px;
    border-radius: 14px;
    position: relative;
    word-break: break-word;
    line-height: 1.7;
    font-size: 14px;
}

.message-bubble.user {
    background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
    color: #fff;
    border-bottom-right-radius: 4px;
    box-shadow: 0 2px 12px rgba(24, 144, 255, 0.2);
}

.message-bubble.assistant {
    background: #fff;
    color: #1a1a2e;
    border-bottom-left-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.06);
    border: 1px solid #f0f0f0;
}

.message-bubble.assistant .message-content {
    white-space: pre-wrap;
}

.thinking-bubble {
    min-width: 120px;
}

.thinking-dots {
    display: flex;
    align-items: center;
    gap: 6px;
}

.thinking-text {
    font-size: 13px;
    color: #8c8c8c;
    margin-left: 4px;
}

.dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #1890ff;
    animation: bounce 1.4s infinite ease-in-out both;
}

.dot:nth-child(1) {
    animation-delay: -0.32s;
}

.dot:nth-child(2) {
    animation-delay: -0.16s;
}

.dot:nth-child(3) {
    animation-delay: 0s;
}

@keyframes bounce {
    0%, 80%, 100% {
        transform: scale(0.6);
        opacity: 0.4;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}

.message-content {
    line-height: 1.7;
}

.message-time {
    font-size: 11px;
    margin-top: 8px;
    opacity: 0.7;
    text-align: right;
    letter-spacing: 0.3px;
}

.message-bubble.user .message-time {
    color: rgba(255, 255, 255, 0.75);
}

.message-bubble.assistant .message-time {
    color: #bfbfbf;
}

.chat-input-area {
    padding: 16px 28px 20px;
    background: #fff;
    border-top: 1px solid #e8e8e8;
    flex-shrink: 0;
}

.input-row {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}

.input-row .ant-textarea {
    flex: 1;
    border-radius: 10px;
    border-color: #d9d9d9;
    resize: none;
    font-size: 14px;
    padding: 10px 14px;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.input-row .ant-textarea:hover,
.input-row .ant-textarea:focus {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.06);
}

.send-btn {
    height: 78px;
    min-width: 92px;
    border-radius: 10px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
    transition: all 0.25s ease;
}

.send-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(24, 144, 255, 0.3);
}

.case-tag {
    cursor: pointer;
    font-weight: 700;
    font-size: 12px;
    padding: 3px 12px 3px 10px;
    border-radius: 6px;
    background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%);
    border: 1.5px solid #69c0ff;
    color: #0050b3;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    display: inline-block;
    margin: 2px 3px;
    line-height: 1.8;
    position: relative;
    letter-spacing: 0.3px;
}

.case-tag::before {
    content: '📎';
    font-size: 11px;
    margin-right: 4px;
    display: inline-block;
}

.case-tag:hover {
    background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
    color: #fff;
    border-color: #096dd9;
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 4px 14px rgba(24, 144, 255, 0.4);
}

.case-tag:active {
    transform: translateY(0) scale(0.97);
}

.case-modal-spin {
    min-height: 200px;
}

.case-detail-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;
}

.case-detail-badge {
    display: inline-block;
    padding: 4px 12px;
    background: #e6f7ff;
    color: #1890ff;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.case-detail-no {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a2e;
}

.case-empty {
    text-align: center;
    padding: 60px 0;
    color: #8c8c8c;
    font-size: 14px;
}

.case-detail .ant-descriptions-item-label {
    font-weight: 600;
    color: #595959;
    width: 120px;
    white-space: nowrap;
    background: #fafafa;
}

.case-detail .ant-descriptions-item-content {
    color: #1a1a2e;
}
</style>
